import { supabase } from '@/lib/supabaseClient';
import { SubscriptionTier, SubscriptionSummary, CompanyInvitation, CreateInvitationInput, UserRole } from '@/types';
import type { SubscriptionTierRow, CompanyInvitationRow } from '@/types/supabase';
import { logger } from '@/utils/logger';

export class SubscriptionService {
    // ──────────────────────────────
    // Subscription Tiers
    // ──────────────────────────────

    static async getTiers(): Promise<SubscriptionTier[]> {
        const { data, error } = await supabase
            .from('subscription_tiers')
            .select('*')
            .eq('is_active', true)
            .order('price_monthly', { ascending: true });

        if (error) throw error;

        // Mapear y ordenar por ID explícito: Starter -> Growth -> Pro
        const order = ['starter', 'pro', 'enterprise'];
        const mapped = (data || []).map(this.mapTierFromDB);
        return mapped.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
    }

    static async getTierById(id: string): Promise<SubscriptionTier | null> {
        const { data, error } = await supabase
            .from('subscription_tiers')
            .select('*')
            .eq('id', id)
            .single();

        if (error) return null;
        return data ? this.mapTierFromDB(data) : null;
    }

    // ──────────────────────────────
    // Subscription Summary (RPC)
    // ──────────────────────────────

    static async getSubscriptionSummary(): Promise<SubscriptionSummary | null> {
        const { data, error } = await supabase
            .rpc('get_company_subscription_summary');

        if (error) {
            logger.warn('getSubscriptionSummary error: ' + error.message);
            return null;
        }
        if (!data || !Array.isArray(data) || data.length === 0) return null;

        const row = data[0];
        return {
            companyId: row.company_id,
            tierId: row.tier_id,
            tierName: row.tier_name,
            subscriptionStatus: row.subscription_status,
            trialEndsAt: row.trial_ends_at,
            maxTechnicians: row.max_technicians,
            currentTechnicians: Number(row.current_technicians),
            maxOrdersMonthly: row.max_orders_monthly,
            currentOrdersMonthly: Number(row.current_orders_monthly),
        };
    }

    // ──────────────────────────────
    // Company Invitations
    // ──────────────────────────────

    static async createInvitation(input: CreateInvitationInput): Promise<CompanyInvitation> {
        let companyId = input.companyId;

        if (!companyId) {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                const { data: tech } = await supabase
                    .from('technicians')
                    .select('company_id')
                    .eq('auth_user_id', user.id)
                    .single();
                if (tech?.company_id) {
                    companyId = tech.company_id;
                }
            }
        }

        if (!companyId) {
            throw new Error('No se pudo determinar el company_id para la invitación');
        }

        const token = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
        const email = input.email.toLowerCase().trim();

        // 0. Validar que el email no pertenece al administrador de la empresa
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (currentUser?.email && currentUser.email.toLowerCase().trim() === email) {
            throw new Error('El administrador de la empresa ya tiene cuenta y no puede ser añadido como técnico.');
        }

        const { data: targetCompany } = await supabase
            .from('companies')
            .select('admin_email')
            .eq('id', companyId)
            .maybeSingle();

        if (targetCompany?.admin_email && targetCompany.admin_email.toLowerCase().trim() === email) {
            throw new Error('El administrador de la empresa ya tiene cuenta y no puede ser añadido como técnico.');
        }

        // 0.1 Validar que no exista ya un técnico en la empresa con ese email
        const { data: existingTech } = await supabase
            .from('technicians')
            .select('id, name')
            .eq('company_id', companyId)
            .ilike('email', email)
            .maybeSingle();

        if (existingTech) {
            throw new Error(`Este correo ya está registrado para el técnico "${existingTech.name}" en tu equipo.`);
        }

        // 1. Crear o actualizar técnico (visible inmediatamente en el dashboard)
        const { error: techError } = await supabase.rpc('create_technician_secure', {
            p_company_id: companyId,
            p_email: email,
            p_name: input.name || email.split('@')[0],
            p_phone: input.phone || null,
            p_role: 'technician',
            p_status: 'pending',
            p_category: input.category || 'Operari@',
            p_specialties: input.specialties || [],
            p_color: input.color || '#3b82f6',
            p_avatar_url: null
        });

        if (techError) {
            logger.error('Error in create_technician_secure', techError);
            if (techError.message?.includes('technicians_email_company_unique') || techError.code === '23505') {
                throw new Error('Este email ya está registrado para un técnico en tu equipo.');
            }
            throw techError;
        }

        // 2. Guardar token de invitación
        const { data, error } = await supabase
            .from('company_invitations')
            .insert({
                email,
                role: input.role || 'technician',
                token,
                company_id: companyId,
                expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            })
            .select()
            .single();

        if (error) throw error;

        // Obtener el nombre de la empresa para pasarlo al email
        let companyName = '';
        try {
            const { data: cData } = await supabase.from('companies').select('name').eq('id', companyId).maybeSingle();
            if (cData?.name) companyName = cData.name;
        } catch {
            // Ignorar error si no se encuentra la empresa
        }

        // 3. Enviar email de invitación vía Edge Function
        try {
            // V2235: Robust siteUrl for invitations - handle localhost vs production
            const isLocalDev = typeof window !== 'undefined' && 
                              (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
                              window.location.port !== '';
            const siteUrl = isLocalDev ? window.location.origin : 'https://partediario.app';
            
            // Get current admin info for the email
            const { data: { user: adminUser } } = await supabase.auth.getUser();
            const adminName = adminUser?.user_metadata?.full_name || 
                             adminUser?.user_metadata?.name || 
                             'Tu administrador';

            if (!companyName && adminUser?.user_metadata?.company_name) {
                companyName = adminUser.user_metadata.company_name;
            }

            logger.info(`[SubscriptionService] Sending invitation to ${email} for company ${companyName || 'tu empresa'} via ${siteUrl}`);

            await supabase.functions.invoke('auth-emails', {
                body: {
                    user: {
                        email,
                        user_metadata: {
                            role: 'technician',
                            full_name: input.name?.trim() || email.split('@')[0],
                            company_id: companyId,
                            company_name: companyName || undefined,
                        },
                    },
                    email_data: {
                        email_action_type: 'invite',
                        token: token,
                        token_hash: token,
                        confirmation_url: `${siteUrl}/join?token=${token}`,
                        company_id: companyId,
                        company_name: companyName,
                        admin_name: adminName
                    },
                },
            });
        } catch (emailErr: unknown) {
            const errMsg = emailErr instanceof Error ? emailErr.message : String(emailErr);
            logger.warn('Error enviando email de invitación: ' + errMsg);
            // No bloqueamos el flujo si el email falla
        }

        return this.mapInvitationFromDB(data);
    }

    static async getInvitationByToken(token: string): Promise<CompanyInvitation | null> {
        const { data, error } = await supabase
            .rpc('get_invitation_info', { p_token: token })
            .maybeSingle();

        if (error) {
            logger.error('getInvitationByToken error', error);
            return null;
        }
        
        if (!data) return null;

        const info = data as unknown as {
            id: string;
            company_id: string;
            company_name: string;
            email: string;
            role: string;
            token: string;
            expires_at: string;
            created_at?: string;
        };

        return {
            id: info.id,
            companyId: info.company_id,
            email: info.email,
            role: (info.role || 'technician') as UserRole,
            token: info.token,
            expiresAt: info.expires_at,
            createdAt: info.created_at || new Date().toISOString(),
            companies: { name: info.company_name }
        };
    }

    static async getPendingInvitations(): Promise<CompanyInvitation[]> {
        const { data, error } = await supabase
            .from('company_invitations')
            .select('*')
            .is('accepted_at', null)
            .gt('expires_at', new Date().toISOString())
            .order('created_at', { ascending: false });

        if (error) throw error;
        return (data || []).map(this.mapInvitationFromDB);
    }

    static async deleteInvitation(id: string): Promise<void> {
        const { error } = await supabase
            .from('company_invitations')
            .delete()
            .eq('id', id);

        if (error) throw error;
    }

    // ──────────────────────────────
    // Helpers
    // ──────────────────────────────

    private static mapTierFromDB(row: SubscriptionTierRow): SubscriptionTier {
        return {
            id: row.id,
            name: row.name,
            description: row.description || '',
            priceMonthly: row.price_monthly,
            priceYearly: row.price_yearly,
            maxTechnicians: row.max_technicians,
            maxOrdersMonthly: row.max_orders_monthly,
            maxAiMinutesMonthly: row.max_ai_minutes_monthly,
            features: row.features || [],
            stripePriceIdMonthly: row.stripe_price_id_monthly || undefined,
            stripePriceIdYearly: row.stripe_price_id_yearly || undefined,
        };
    }

    private static mapInvitationFromDB(row: CompanyInvitationRow): CompanyInvitation {
        return {
            id: row.id,
            companyId: row.company_id,
            email: row.email,
            role: (row.role || 'technician') as UserRole,
            token: row.token,
            expiresAt: row.expires_at,
            acceptedAt: row.accepted_at,
            createdAt: row.created_at,
            companies: row.companies,
        };
    }

    static async acceptInvitation(token: string): Promise<void> {
        const { error } = await supabase.rpc('accept_invitation', { p_token: token });
        if (error) throw error;
    }

    // ──────────────────────────────
    // Collaborator / Beta Activation (Paywall Bypass)
    // ──────────────────────────────

    static COLLABORATOR_CODES = ['BETA2026', 'COLABORADOR2026', 'SOCIO2026', 'AMIGO2026'];

    static isValidCollaboratorCode(code: string): boolean {
        if (!code) return false;
        return this.COLLABORATOR_CODES.includes(code.trim().toUpperCase());
    }

    static async validateCollaboratorLimit(code: string): Promise<{ success: boolean; codeValid: boolean; limitReached: boolean; message: string; count?: number; maxLimit?: number }> {
        const { data, error } = await supabase
            .rpc('check_collaborator_limit', { p_code: code });

        if (error) {
            logger.error('[SubscriptionService] Error calling check_collaborator_limit', error);
            const localValid = this.isValidCollaboratorCode(code);
            return {
                success: localValid,
                codeValid: localValid,
                limitReached: false,
                message: localValid 
                  ? 'Código válido (validación local).' 
                  : 'Código no válido.'
            };
        }

        return {
            success: data.success,
            codeValid: data.code_valid,
            limitReached: data.limit_reached,
            message: data.message,
            count: data.count,
            maxLimit: data.max_limit
        };
    }


    static async activateCollaboratorPlan(tierId: string): Promise<boolean> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No se encontró un usuario autenticado');

        const { error } = await supabase
            .from('companies')
            .update({
                current_tier_id: tierId,
                subscription_status: 'active',
                trial_ends_at: null
            })
            .eq('owner_user_id', user.id);

        if (error) {
            logger.error('[SubscriptionService] Error al activar plan de colaborador', error);
            throw error;
        }

        return true;
    }
}

